import React from 'react';
import { updateAbpDropdowns, AbpLevel, fetchAbpData, AbpTableRow, fetchAbpYears } from './api';
import Dropdowns from 'components/Dropdowns';
import DataTable from '../../components/pnl-monthly-table';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import { useAuth } from 'contexts/AuthContext';

function useStableRef<T>(initial: T) {
  const ref = React.useRef(initial);
  return ref;
}

const AbpPage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [years, setYears] = React.useState<string[]>([]);
  const [year, setYear] = React.useState<string>('');
  const [levels, setLevels] = React.useState<AbpLevel[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>('');
  const [tableData, setTableData] = React.useState<AbpTableRow[]>([]);
  const [tableLoading, setTableLoading] = React.useState<boolean>(false);
  const [tableError, setTableError] = React.useState<string>('');
  const [conversionType, setConversionType] = React.useState<'INR' | 'Lakhs' | 'Crores'>('Lakhs');
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);

  const didInitRef = useStableRef(false);
  const { permissions } = useAuth();

  const fetchLevels = React.useCallback(async (selectionMap?: Record<string, string[]>) => {
    if (!year) return;
    setLoading(true);
    setError('');
    try {
      const data = await updateAbpDropdowns({ year, selection: selectionMap || {} });
      setLevels(Array.isArray(data?.levels) ? data.levels : []);
    } catch (err: unknown) {
      const isForbidden = (err as any)?.status === 403;
      const message = err instanceof Error ? err.message : isForbidden ? 'You are not allowed to access this resource.' : 'Something went wrong';
      setError(message);
      if (isForbidden) setLevels([]);
    } finally {
      setLoading(false);
    }
  }, [year]);

  const buildFiltersFromLevels = React.useCallback((currentLevels: AbpLevel[]) => {
    const filters: Record<string, string[]> = {};
    for (const level of currentLevels) {
      if (level && level.selected && level.selected.length > 0) {
        filters[String(level.id)] = level.selected;
      }
    }
    return filters;
  }, []);

  const fetchTableData = React.useCallback(async (currentLevels: AbpLevel[]) => {
    if (!year) return;
    const filters = buildFiltersFromLevels(currentLevels);
    
    setTableLoading(true);
    setTableError('');
    try {
      const data = await fetchAbpData({ year, filters });
      setTableData(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err: unknown) {
      const isForbidden = (err as any)?.status === 403;
      const message = err instanceof Error ? err.message : isForbidden ? 'You are not allowed to access this resource.' : 'Failed to fetch table data';
      setTableError(message);
      if (isForbidden) setTableData([]);
    } finally {
      setTableLoading(false);
      setHasLoadedOnce(true);
    }
  }, [buildFiltersFromLevels, year]);

  React.useEffect(() => {
    setBreadcrumbs([
      { label: 'Reports', href: '/reports/abp' },
      { label: 'Annual Business Plan' },
    ]);
    if (didInitRef.current) return;
    didInitRef.current = true;
    (async () => {
      try {
        const resp = await fetchAbpYears();
        const yrList = Array.isArray(resp?.years) ? resp.years : [];
        setYears(yrList);
        setYear(yrList[0] || '');
      } catch (e) {
        setYears([]);
        setYear('');
      }
    })();
  }, [didInitRef, setBreadcrumbs]);

  // When year changes, refresh filters and table
  React.useEffect(() => {
    if (!year) return;
    setLevels([]);
    fetchLevels({});
    fetchTableData([]);
  }, [year, fetchLevels, fetchTableData]);

  const buildSelectionFromLevels = React.useCallback((currentLevels: AbpLevel[]) => {
    const map: Record<string, string[]> = {};
    for (const level of currentLevels) {
      if (level && level.selected && level.selected.length > 0) {
        map[String(level.id)] = level.selected;
      }
    }
    return map;
  }, []);

  const handleSelectChange = React.useCallback((levelId: number | string, value: string[]) => {
    const desc = (levels || [])
      .filter((l) => Boolean(l?.visible))
      .sort((a, b) => Number(b.id) - Number(a.id));
    const indexById = Object.fromEntries(desc.map((l, i) => [String(l.id), i]));
    const changedIdx = indexById[String(levelId)];

    const nextLevels = levels.map((lvl) => {
      const idx = indexById[String(lvl.id)];
      if (idx < changedIdx) {
        return lvl.id === levelId ? { ...lvl, selected: value.length > 0 ? value : null } : lvl;
      }
      if (idx === changedIdx) {
        return { ...lvl, selected: value.length > 0 ? value : null };
      }
      return { ...lvl, selected: null };
    });

    setLevels(nextLevels);

    const selection = buildSelectionFromLevels(nextLevels);
    if (value.length === 0) delete selection[String(levelId)];
    fetchLevels(selection);
    
    // Fetch table data with the updated levels
    fetchTableData(nextLevels);
  }, [levels, buildSelectionFromLevels, fetchLevels, fetchTableData]);

  const handleConversionChange = React.useCallback((type: 'INR' | 'Lakhs' | 'Crores') => {
    setConversionType(type);
  }, []);

  const handleYearChange = React.useCallback((newYear: string) => {
    if (!newYear) return; // enforce at least one year selected
    setYear(newYear);
  }, []);

  const handleBatchSelect = React.useCallback((updates: Array<{ levelId: number | string; value: string[] }>) => {
    if (!Array.isArray(updates) || updates.length === 0) return;

    const updatesById = Object.fromEntries(updates.map(u => [String(u.levelId), u.value]));

    const nextLevels = (levels || []).map((lvl) => {
      const updated = updatesById[String(lvl.id)];
      if (updated) {
        return { ...lvl, selected: updated };
      }
      return lvl;
    });

    setLevels(nextLevels);

    const selection = buildSelectionFromLevels(nextLevels);
    fetchLevels(selection);
    fetchTableData(nextLevels);
  }, [levels, buildSelectionFromLevels, fetchLevels, fetchTableData]);

  const canDownloadAbp = React.useMemo(() => {
    return Array.isArray(permissions) && permissions.includes('abp.download');
  }, [permissions]);

  return (
    <div className="space-y-6 mx-auto w-full max-w-screen-2xl 2xl:max-w-[2000px] px-4 sm:px-6">
      {/* Filters Section */}
      <div>
        <Dropdowns
          year={year}
          years={years}
          levels={levels}
          loading={loading}
          onSelectChange={handleSelectChange}
          onBatchSelect={handleBatchSelect}
          onYearChange={handleYearChange}
        />
        
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}
      </div>

      {/* Table Section */}
      <DataTable
        data={tableData}
        loading={tableLoading}
        conversionType={conversionType}
        onConversionChange={handleConversionChange}
        showDownloadButton={canDownloadAbp}
        dataType="ABP"
        hasLoadedOnce={hasLoadedOnce}
      />

      {tableError && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600 text-sm">{tableError}</p>
        </div>
      )}
    </div>
  );
};

export default AbpPage;
